import FileCopy from '@mui/icons-material/FileCopy'
import { Box, IconButton } from '@mui/material'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Icon from '@mui/material/Icon'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { path, pathOr } from 'ramda'
import React from 'react'

import Accordion from '@/app/component/atom/accordion'
import EditableText from '@/app/component/atom/form/editable-text'
import Tooltip from '@/app/component/atom/tooltip'
import Conditions from '@/app/component/conditions'
import { Groups } from '@/app/component/conditions/definitions'
import { getInitialWaTab, isWhatsApp } from '@/app/module/campaigns/component/item/steps/content/whatsapp-utils'
import { RUNNING_CAMPAIGNS_DISABLED_TOOLTIP, TYPES } from '@/app/module/campaigns/definitions'
import { BaseLanguageType } from '@/app/module/campaigns/helpers'
import { getMessagePlaceholderLabel } from '@/app/module/campaigns/label-helpers'
import { processConditions } from '@/app/module/campaigns/store/selectors'
import {
  CampaignFileResult,
  CampaignTemplateType,
  ConditionActions,
  Language,
  LegacyCampaignItem,
  Offset,
  PartFamily,
  ReminderMessage,
  SenderIdOption,
  Message as TMessage,
} from '@/app/module/campaigns/types'
import { countActionsInCallResult } from '@/app/module/campaigns/utils/actions/list'
import { WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import { NotificationsData } from '@/app/module/notifications/types'
import {
  CountryTimezone,
  FilesType,
  FileUploadType,
  PersonalizationType,
  SelectOption,
  TimezoneWithCountryCodeType,
} from '@/app/types'

import CallResults from './components/call-results'
import Delay from './delay'
import { isNewMessage } from './helpers'
import { LibrarySaveButton } from './library-save-button'
import { MessageActions } from './message-actions'
import MessageContent from './message-content'
import MessageResponse from './message-response'
import ReminderOffset from './reminder-offset'

type BaseProps = {
  apiPersonalizationList: PersonalizationType
  messageIds: string[]
  conditions?: number
  countriesTimezones: CountryTimezone[]
  countryCode: string
  defaultLanguage: string
  files: {
    data: FilesType
    loadingItems: string[]
  }
  groups: Groups
  hasQuestion?: boolean
  id: string
  index: number
  languages: Language[]
  loading: boolean
  messageRef?: React.Ref<HTMLDivElement>
  newItems: string[]
  orgId: number
  page: number
  pageSize: number
  personalizationList: PersonalizationType
  published: boolean
  row: number
  segments: Groups
  senderIds: SenderIdOption[]
  supportedMessageTypes: SelectOption<string>[]
  timezone: string
  timezones: TimezoneWithCountryCodeType
  token: string
  total: number
  voiceList: BaseLanguageType[]
  voicePlayingId?: string
  whatsappSenderIds: SenderIdOption[]

  createNotification: (data: NotificationsData) => void
  getFile: (id: string, file: string) => Promise<CampaignFileResult>
  onChange: (item: Partial<TMessage> & { id: string }, changed?: boolean) => void
  onClose: (id: string) => void
  onMessageCreate: (index: number, row: number, message: TMessage | ReminderMessage) => void
  onMessageDuplicate: (id: string, row: number) => void
  onMicAccessError: () => void
  onSaveConditions: (item: TMessage) => void
  saveFile: (id: string, file: FileUploadType) => Promise<CampaignFileResult>
  setConditions: (conditions?: number) => void
  setLoading: (loading: boolean) => void
  setPlayingId: (playId?: string) => void
}

type CampaignProps = BaseProps & {
  campaignType: LegacyCampaignItem['type']
  message: TMessage
}

type ReminderProps = BaseProps & {
  campaignType: CampaignTemplateType.Reminder
  message: ReminderMessage
}

type Props = CampaignProps | ReminderProps

type State = {
  whatsappTab: WhatsAppTab
}

const defaultArray: any[] = []

export const getDelayTimingLabel = (type: TMessage['type']) => {
  if (type === PartFamily.API) {
    return 'make this API request'
  }

  if (type === PartFamily.QuotaActions) {
    return 'run quota actions'
  }

  return 'send this message'
}

class MessageWrapper extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      whatsappTab: isWhatsApp(props.message) ? getInitialWaTab(props.message.message) : WhatsAppTab.Text,
    }
  }

  setWhatsappTab = (tab: WhatsAppTab) => this.setState({ whatsappTab: tab })

  defaultObject = {}

  defaultFunc = () => this.defaultObject

  callResultActionChangeHandler = ({ value }: { value: ConditionActions[] }) =>
    this.props.onChange({
      ...this.props.message,
      callResultActions: value,
    })

  getFileHandler = (file: string) => this.props.getFile(this.props.id, file)

  saveFileHandler = (item: FileUploadType) => this.props.saveFile(this.props.id, item)

  handleContentChange = (value: Partial<TMessage>, changed?: boolean) =>
    this.props.onChange({ ...value, id: this.props.id }, changed)

  handleLabelChange = (value: string) => {
    const { message, onChange } = this.props
    onChange(
      {
        ...message,
        label: value,
      },
      true,
    )
  }

  render() {
    const {
      messageIds,
      campaignType,
      conditions,
      countriesTimezones,
      countryCode,
      defaultLanguage,
      files,
      groups,
      hasQuestion,
      id,
      index,
      languages,
      message,
      newItems,
      orgId,
      personalizationList,
      published,
      messageRef,
      row,
      segments,
      senderIds,
      supportedMessageTypes,
      timezone,
      timezones,
      token,
      total,
      voiceList,
      voicePlayingId,
      whatsappSenderIds,
      getFile,
      onChange,
      onClose,
      onMessageCreate,
      onMessageDuplicate,
      onMicAccessError,
      onSaveConditions,
      saveFile,
      setConditions,
      setLoading,
      setPlayingId,
    } = this.props
    const { type, label } = message
    const newMessage = isNewMessage({ newItems, id })

    const { label: placeHolderLabel, conditionsTitle } = getMessagePlaceholderLabel(type, hasQuestion)
    let callResultActionsCount = 0
    const callResultActionsPath = path<string[]>(
      ['callResultActionsPath'],
      TYPES[campaignType.toUpperCase() as keyof typeof TYPES],
    )
    const callResultActions = !callResultActionsPath
      ? defaultArray
      : pathOr(defaultArray, callResultActionsPath, message)

    if (campaignType === 'ivr' || campaignType === 'drip') {
      callResultActionsCount = countActionsInCallResult(callResultActions)
    }
    const condition = path(['when'], message) ? 'sometimes' : 'always'

    return (
      <div
        key={id}
        id={`message-${message.id}`}
        data-message-id={message.id}
        ref={messageRef}
        className="message-block"
      >
        <Card style={{ marginBottom: '3rem' }}>
          {conditions === index && (
            <Conditions
              groups={groups || this.defaultObject}
              segments={segments || this.defaultObject}
              data={processConditions(pathOr(['or', ['and', ['', '', '']]], ['when'], message))}
              personalizationList={personalizationList}
              title={conditionsTitle}
              item={message}
              onSaveConditions={onSaveConditions}
              onClose={() => setConditions()}
            />
          )}
          <CardContent>
            <Toolbar
              style={{
                margin: '-24px -24px 0px',
                backgroundColor: '#f5f5f5',
                justifyContent: 'space-between',
              }}
            >
              <Box id={`copy-${index}`} sx={{ marginRight: 2 }}>
                <Tooltip title="Copy Message ID">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(message.id).then(() => {
                        this.props.createNotification({
                          'copy-part-id': {
                            type: 'success',
                            message: 'Message ID copied!',
                          },
                        })
                      })
                    }}
                    edge="end"
                  >
                    <FileCopy />
                  </IconButton>
                </Tooltip>
                <Box id={`message-${index}-id`} sx={{ display: 'none' }}>
                  {message.id}
                </Box>
              </Box>
              <div
                style={{
                  width: '100%',
                }}
              >
                <div
                  style={{
                    marginTop: '7px',
                    marginBottom: '5px',
                    width: '100%',
                  }}
                >
                  <Typography
                    id={`message-label-${index}`}
                    component="div"
                    variant="subtitle1"
                    color="primary"
                    style={{
                      height: '22px',
                      width: '100%',
                    }}
                  >
                    <EditableText
                      data-testid={`message-label-${index}`}
                      displayText={`${index + 1}. ${label || placeHolderLabel}`}
                      maxLength={50}
                      value={label || placeHolderLabel}
                      onChange={this.handleLabelChange}
                    />
                  </Typography>
                </div>
                <div
                  style={{
                    display: 'flex',
                  }}
                >
                  <Typography
                    color="textSecondary"
                    data-testid={`conditions-${index}`}
                    variant="subtitle2"
                    style={{
                      paddingTop: 4,
                    }}
                  >
                    {conditionsTitle}{' '}
                    <b>
                      <i>{condition}</i>.
                    </b>
                  </Typography>
                  <Button
                    color="primary"
                    data-testid={`open-conditions-${index}`}
                    onClick={() => setConditions(index)}
                    size="small"
                  >
                    (click here to {condition === 'always' ? 'set' : 'edit'} conditions)
                  </Button>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <LibrarySaveButton index={index} message={message} />
                <div style={{ marginLeft: '12px' }}>
                  <Tooltip
                    title={selectDeleteTooltip({
                      published,
                      newMessage,
                    })}
                  >
                    <div>
                      <Button
                        id={`delete-message-${index}`}
                        data-testid={`delete-message-${index}`}
                        disabled={published && !newMessage}
                        style={{
                          minWidth: '0',
                          width: '40px',
                          height: '40px',
                          zIndex: '2',
                          borderRadius: '50%',
                        }}
                        onClick={() => onClose(id)}
                      >
                        <Icon>close</Icon>
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </Toolbar>
            <MessageContent
              {...this.props}
              isNewMessage={newMessage}
              whatsappTab={this.state.whatsappTab}
              onChange={this.handleContentChange}
              setWhatsappTab={this.setWhatsappTab}
            />
            <MessageResponse {...this.props} whatsappTab={this.state.whatsappTab} onChange={this.handleContentChange} />
          </CardContent>
          {campaignType === 'drip' && (
            <React.Fragment>
              <Divider />
              <CardContent>
                <Delay
                  isFirst={index === 0}
                  published={published}
                  item={message}
                  message={getDelayTimingLabel(type)}
                  changeHandler={this.handleContentChange}
                  timezones={timezones}
                  timezone={timezone}
                  countriesTimezones={countriesTimezones}
                  countryCode={countryCode}
                  setLoading={setLoading}
                />
              </CardContent>
              {message.type === 'voice' && (
                <Accordion
                  className="call-result-actions-accordion"
                  display={callResultActionsCount > 0}
                  placeholder="Click here to specify what actions we should take if the contact does/ doesn't answer the call"
                  title="Call Result Actions"
                >
                  <CallResults
                    actions={callResultActions}
                    campaignType={campaignType}
                    countriesTimezones={countriesTimezones}
                    countryCode={countryCode}
                    defaultLanguage={defaultLanguage}
                    files={files}
                    languages={languages}
                    orgId={orgId}
                    personalizationList={personalizationList}
                    senderIds={senderIds}
                    token={token}
                    timezone={timezone}
                    timezones={timezones}
                    voiceList={voiceList}
                    voicePlayingId={voicePlayingId}
                    whatsappSenderIds={whatsappSenderIds}
                    changeHandler={this.callResultActionChangeHandler}
                    getFile={getFile}
                    onMicAccessError={onMicAccessError}
                    saveFile={saveFile}
                    setPlayingId={setPlayingId}
                  />
                </Accordion>
              )}
            </React.Fragment>
          )}
          {campaignType === CampaignTemplateType.Reminder && (
            <>
              <Divider />
              <CardContent>
                <ReminderOffset
                  countriesTimezones={countriesTimezones}
                  countryCode={countryCode}
                  messageType={message.type}
                  offset={message.offset}
                  published={published}
                  timezone={timezone}
                  timezones={timezones}
                  onChange={(offset: Offset) =>
                    onChange({
                      ...message,
                      offset,
                    })
                  }
                />
              </CardContent>
            </>
          )}
          <CardActions
            style={{
              backgroundColor: '#f5f5f5',
            }}
          >
            <Box display="flex" justifyContent="flex-end" width="100%">
              <MessageActions
                index={index}
                campaignType={campaignType}
                total={total}
                published={published}
                supportedMessageTypes={supportedMessageTypes}
                messageIds={messageIds}
                timezone={timezone}
                onMessageCreate={(msg) => onMessageCreate(index + 1, row, msg)}
                onMessageDuplicate={() => onMessageDuplicate(id, row)}
              />
            </Box>
          </CardActions>
        </Card>
      </div>
    )
  }
}

const selectDeleteTooltip = ({ published, newMessage }: any) => {
  if (published && !newMessage) {
    return RUNNING_CAMPAIGNS_DISABLED_TOOLTIP
  }
  return 'Delete message'
}

export default MessageWrapper

export type { Props as MessageWrapperProps }
